import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { healthCheck, listDocuments, getSCGRules } from '../services/api';
import { DocumentTextIcon, CogIcon, ScissorsIcon, BeakerIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import ClassificationBadge from '../components/ClassificationBadge';
import SmartRecommendations from '../components/SmartRecommendations';
import QuickClassifier from '../components/QuickClassifier';
import SystemHealthMonitor from '../components/SystemHealthMonitor';

export default function Dashboard() {
  const { data: healthData } = useQuery({
    queryKey: ['health'],
    queryFn: healthCheck,
    refetchInterval: 30000, // Check health every 30 seconds
    retry: 2,
    enabled: true,
  });

  const { data: documentsData } = useQuery({
    queryKey: ['documents-summary'],
    queryFn: () => listDocuments(10),
    retry: 2,
    enabled: true,
  });
  const { data: scgData } = useQuery({
    queryKey: ['scg-summary'],
    queryFn: getSCGRules,
    retry: 2,
    enabled: true,
  });

  const quickActions = [
    {
      name: 'Review Document',
      description: 'Upload and review legal documents for privilege',
      href: '/classify',
      icon: DocumentTextIcon,
      color: 'bg-indigo-500 hover:bg-indigo-600'
    },
    {
      name: 'Discovery Cases',
      description: 'Manage cases, review documents, produce',
      href: '/cases',
      icon: ScissorsIcon,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      name: 'Legal Policies',
      description: 'Manage firm policies and legal guidelines',
      href: '/policies',
      icon: CogIcon,
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      name: 'Testing Suite',
      description: 'Test and optimize privilege classification',
      href: '/testing',
      icon: BeakerIcon,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      name: 'Settings',
      description: 'Configure system parameters and models',
      href: '/settings',
      icon: AdjustmentsHorizontalIcon,
      color: 'bg-gray-500 hover:bg-gray-600'
    }
  ];

  const getClassificationStats = () => {
    if (!documentsData?.documents) return {};
    
    const stats = {};
    documentsData.documents.forEach(doc => {
      const classification = doc.classification?.toUpperCase() || 'UNKNOWN';
      stats[classification] = (stats[classification] || 0) + 1;
    });
    
    return stats;
  };

  const classificationStats = getClassificationStats();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Legal Document Review Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Manage and review legal documents using AI-powered privilege classification
        </p>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${
              healthData?.status === 'healthy' ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
            <span className="text-sm font-medium text-gray-700">
              API Status: {healthData?.status || 'Unknown'}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm font-medium text-gray-700">
              Total Documents: {documentsData?.total || 0}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
            <span className="text-sm font-medium text-gray-700">
              SCG Rules: {scgData?.total_rules || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Top Row - Quick Classifier and Smart Recommendations */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
        <QuickClassifier />
        <SmartRecommendations />
      </div>

      {/* System Health Monitor */}
      <div className="mb-8">
        <SystemHealthMonitor />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="group relative bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
            >
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-md text-white ${action.color}`}>
                <action.icon className="w-5 h-5" />
              </div>
              <div className="mt-3">
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600">
                  {action.name}
                </h3>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Statistics and Recent Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Classification Statistics */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Classification Distribution</h2>
          {Object.keys(classificationStats).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(classificationStats).map(([classification, count]) => (
                <div key={classification} className="flex items-center justify-between">
                  <ClassificationBadge classification={classification} />
                  <span className="text-sm font-medium text-gray-900">{count} documents</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No documents classified yet</p>
          )}
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Documents</h2>
            <Link to="/classify" className="text-sm text-indigo-600 hover:text-indigo-500">
              Classify more
            </Link>
          </div>
          {documentsData?.documents?.length > 0 ? (
            <div className="space-y-3">
              {documentsData.documents.slice(0, 5).map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.title || 'Untitled Document'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {doc.metadata?.word_count || 0} words
                    </p>
                  </div>
                  <ClassificationBadge 
                    classification={doc.classification}
                    fullClassification={doc.full_classification}
                    caveats={doc.caveats}
                    releasability={doc.releasability}
                    confidence={doc.confidence}
                    className="ml-2"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No documents available</p>
          )}
        </div>
      </div>
    </div>
  );
}